import React from "react";
import {
  ContentCopy,
  Reply,
  Delete,
  Download,
  Close,
  Edit,
} from "@mui/icons-material";

const MessageOptionsModal = ({
  isOpen,
  onClose,
  message,
  isMine,
  onCopy,
  onReply,
  onDelete,
  onDownload,
  onEdit,
}) => {
  if (!isOpen || !message) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const hasFiles =
    (message.files && message.files.length > 0) || message.fileUrl;
  const hasDownloadableFiles =
    hasFiles &&
    ((message.files && message.files.length > 0) ||
      (message.fileUrl && message.fileType !== "none"));

  const EDIT_TIME_LIMIT = 25 * 60 * 1000;
  const messageAge = Date.now() - new Date(message.createdAt).getTime();
  const canEdit =
    isMine &&
    message.messageType === "text" &&
    messageAge < EDIT_TIME_LIMIT &&
    message.content &&
    message.content.trim() !== "";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-80 max-w-[90vw] mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Message Options
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Close className="w-6 h-6" />
          </button>
        </div>

        {/* Message Preview */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="text-xs text-gray-500 mb-2">
            From: {message.sender?.username || "Unknown"}
            {message.edited && (
              <span className="ml-2 text-gray-400 italic">(edited)</span>
            )}
          </div>
          <div className="text-sm text-gray-700 max-h-20 overflow-y-auto">
            {hasFiles ? (
              <div className="flex items-center gap-2">
                <span className="text-blue-600">📎</span>
                <span>
                  {message.files && message.files.length > 1
                    ? `${message.files.length} files`
                    : message.fileType === "image" ||
                        message.files?.[0]?.type === "image"
                      ? "Photo"
                      : message.fileType === "video" ||
                          message.files?.[0]?.type === "video"
                        ? "Video"
                        : message.fileType === "audio" ||
                            message.files?.[0]?.type === "audio"
                          ? "Audio"
                          : "Document"}
                </span>
              </div>
            ) : (
              <div className="break-words">
                {message.content || "No content"}
              </div>
            )}
          </div>
        </div>

        {/* Options */}
        <div className="py-2">
          {/* edit */}
          {canEdit && (
            <button
              onClick={() => {
                onEdit(message);
                onClose();
              }}
              className="flex items-center gap-3 w-full px-6 py-4 hover:bg-gray-50 text-sm text-gray-700 transition-colors"
            >
              <Edit className="w-5 h-5 text-gray-600" />
              <span>Edit message</span>
            </button>
          )}
          {/* copy */}
          {message.content && (
            <button
              onClick={() => {
                onCopy(message.content);
                onClose();
              }}
              className="flex items-center gap-3 w-full px-6 py-4 hover:bg-gray-50 text-sm text-gray-700 transition-colors"
            >
              <ContentCopy className="w-5 h-5 text-gray-600" />
              <span>Copy text</span>
            </button>
          )}

          {/* Reply */}
          <button
            onClick={() => {
              onReply(message);
              onClose();
            }}
            className="flex items-center gap-3 w-full px-6 py-4 hover:bg-gray-50 text-sm text-gray-700 transition-colors"
          >
            <Reply className="w-5 h-5 text-gray-600" />
            <span>Reply</span>
          </button>

          {/* Download */}
          {hasDownloadableFiles && (
            <button
              onClick={() => {
                // Handle multiple files (new format)
                if (message.files && message.files.length > 0) {
                  message.files.forEach((file) => {
                    const fileName =
                      file.name || file.url.split("/").pop() || "file";
                    onDownload(file.url, fileName);
                  });
                }
                else if (message.fileUrl) {
                  const fileName = message.fileUrl.split("/").pop() || "file";
                  onDownload(message.fileUrl, fileName);
                }
                onClose();
              }}
              className="flex items-center gap-3 w-full px-6 py-4 hover:bg-gray-50 text-sm text-gray-700 transition-colors"
            >
              <Download className="w-5 h-5 text-gray-600" />
              <span>
                {message.files && message.files.length > 1
                  ? `Download ${message.files.length} files`
                  : "Download"}
              </span>
            </button>
          )}

          {/* Delete - only for own messages */}
          {isMine && (
            <>
              <hr className="my-2 mx-4" />
              <button
                onClick={() => {
                  onDelete(message._id);
                  onClose();
                }}
                className="flex items-center gap-3 w-full px-6 py-4 text-red-600 hover:bg-red-50 text-sm transition-colors"
              >
                <Delete className="w-5 h-5" />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>

        {/* Cancel */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageOptionsModal;
